import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Button, Text, Panel, Switch, H3, Fieldset, FormGroup, Radio, Box, Checkbox } from '@bigcommerce/big-design';

import { updateSettings } from '../../redux/actions/settings';
import { generateAllBarcodes } from '../../redux/actions/barcodes';
import { paymentPortalSession } from '../../redux/actions/payments';
import ToolPage from '../partials/ToolPage';

import UploadStoreLogo from './UploadStoreLogo';

const StoreSettings = ({ payload, settings, submitSettings, generateAll, getPortalSession, barcodes }) => {
  const [submitted, setSubmitted] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(true);

  const [settingsForm, setSettingsForm] = useState(settings);
  const [allowQuantityUpdates, setAllowQuantityUpdates] = useState(settings['allow_quantity_updates']);
  const [enableCustomFields, setEnableCustomFields] = useState(settings['enable_custom_fields']);
  const [enableBPNLocation, setEnableBPNLocation] = useState(settings['enable_bpn_location']);
  const [receiptEmails, setReceiptEmails] = useState(settings['receipt_emails']);

  const [barcodeImageGenerationField, setBarcodeImageGenerationField] = useState(settings['barcode_image_generation_field']);
  const [barcodeImageSymbolType, setBarcodeImageSymbolType] = useState(settings['barcode_image_symbol_type']);
  const [barcodeSymbolType, setBarcodeSymbolType] = useState(settings['barcode_symbol_field']);

  const [acceptGenerateAllRisks, setAcceptGenerateAllRisks] = useState(false);
  const [submittedGenerateAll, setSubmittedGenerateAll] = useState(false);

  useEffect(() => {
    setSubmitted(false);
  }, [settings]);

  const toggleAllowQuantitySwitch = () => {
    let updatedSettings = settingsForm;
    updatedSettings['allow_quantity_updates'] = !allowQuantityUpdates;

    setAllowQuantityUpdates(!allowQuantityUpdates);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const toggleEnableCustomFields = () => {
    let updatedSettings = settingsForm;
    updatedSettings['enable_custom_fields'] = !enableCustomFields;

    setEnableCustomFields(!enableCustomFields);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const toggleEnableBPNLocation = () => {
    let updatedSettings = settingsForm;
    updatedSettings['enable_bpn_location'] = !enableBPNLocation;

    setEnableBPNLocation(!enableBPNLocation);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const toggleReceiptEmails = () => {
    let updatedSettings = settingsForm;
    updatedSettings['receipt_emails'] = !receiptEmails;

    setReceiptEmails(!receiptEmails);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const handleImageGenerationField = (event) => {
    let updatedSettings = settingsForm;
    updatedSettings['barcode_image_generation_field'] = event.target.value;

    setBarcodeImageGenerationField(event.target.value);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const handleImageSymbolType = (event) => {
    let updatedSettings = settingsForm;
    updatedSettings['barcode_image_symbol_type'] = event.target.value;

    setBarcodeImageSymbolType(event.target.value);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const handleSymbolType = (event) => {
    let updatedSettings = settingsForm;
    updatedSettings['barcode_symbol_field'] = event.target.value;

    setBarcodeSymbolType(event.target.value);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }


  const openPortal = () => {
    // Open the tab synchronously - browsers block window.open calls made
    // after an async boundary, so the portal URL fills it in once the
    // session request resolves. Falls back to navigating this tab when
    // the new window was blocked.
    const portalWindow = window.open('', '_blank');

    getPortalSession(payload)
      .then((response) => {
        const url = response.data.portal_url;

        if (portalWindow && url) {
          portalWindow.location.href = url;
        } else if (url) {
          window.location.href = url;
        }
      })
      .catch(() => {
        if (portalWindow) {
          portalWindow.close();
        }
      });
  }

  const handleSubmit = () => {
    setSubmitted(true);
    submitSettings(payload, settingsForm);
    setSettingsSaved(true);
  }

  const handleGenerateAll = () => {
    if (barcodes.status === 'requesting' || submittedGenerateAll) { return; }

    setSubmittedGenerateAll(true);
    generateAll(payload);
  }

  const description = () => {
    return (
      <>
        <Text>App settings control how barcodes are generated and which fields appear on labels.</Text>
        <Text>The "Generate All Barcodes" tool regenerates barcode images for your entire catalog. It is a destructive action and is kept here in settings rather than on the label printing tools.</Text>
      </>
    )
  }

  return(
    <>
      <ToolPage title="App Settings" description={description()} helpLink="bigcommerce/">
        <UploadStoreLogo 
          payload={payload}
        />
        <hr/>
        <Panel>
          <H3>Allow Pick Order Quantity Updates</H3>
          <Switch checked={allowQuantityUpdates} onChange={toggleAllowQuantitySwitch} />
        </Panel>
        <Panel>
          <Box
            backgroundColor="warning10"
            border="box"
            borderRadius="normal"
            padding="medium"
          >
            <Text>Custom fields are not currently available on warehouse receiving order labels sent by the Supplier Purchase Orders app.</Text>
          </Box>

          <H3>Enable Custom Fields</H3>
          <Text>Allow custom fields in label templates. Specify custom fields by name to have them appear on labels.</Text>
          
          <Switch checked={enableCustomFields} onChange={toggleEnableCustomFields} />
        </Panel>
        <Panel>
          <H3>Enable Bin Picking Number Location Selection</H3>
          <Text>Pick locations when generating labels, using the Bin Picking Number from the selected location instead of the default location.</Text>

          <Switch checked={enableBPNLocation} onChange={toggleEnableBPNLocation} />
        </Panel>
        <Panel>
          <H3>Barcode Settings</H3>

          <Fieldset legend="Barcode Image Generation Field">
            <FormGroup>
              <Radio
                checked={!barcodeImageGenerationField || barcodeImageGenerationField === 'sku'}
                label="SKU"
                onChange={handleImageGenerationField}
                value="sku"
              />
              <Radio
                checked={barcodeImageGenerationField === 'upc'}
                label="UPC/EAN"
                onChange={handleImageGenerationField}
                value="upc"
              />
            </FormGroup>
          </Fieldset>
          <Fieldset legend="Barcode Image Type">
            <FormGroup>
              <Radio
                checked={!barcodeImageSymbolType || barcodeImageSymbolType === 'code128'}
                label="Code 128"
                onChange={handleImageSymbolType}
                value="code128"
              />
              <Radio
                checked={barcodeImageSymbolType === 'upca'}
                label="UPCA"
                onChange={handleImageSymbolType}
                value="upca"
              />
              <Radio
                checked={barcodeImageSymbolType === 'ean13'}
                label="EAN13"
                onChange={handleImageSymbolType}
                value="ean13"
              />
              <Radio
                checked={barcodeImageSymbolType === 'ean8'}
                label="EAN8"
                onChange={handleImageSymbolType}
                value="ean8"
              />
            </FormGroup>
          </Fieldset>
          <Fieldset legend="Barcode Symbol Field">
            <FormGroup>
              <Radio
                checked={!barcodeSymbolType || barcodeSymbolType === 'sku'}
                label="SKU"
                onChange={handleSymbolType}
                value="sku"
              />
              <Radio
                checked={barcodeSymbolType === 'upc'}
                label="UPC/EAN"
                onChange={handleSymbolType}
                value="upc"
              />
            </FormGroup>
          </Fieldset>
        </Panel>
        <Panel>
          <Button onClick={() => handleSubmit()} disabled={submitted || settingsSaved}>Save Settings</Button>
          { !settingsSaved &&
            <Text color="warning50">Settings changes have not been saved</Text>
          }
        </Panel>
        <Panel>
          <H3>Payment Settings and Invoices</H3>
          <Text>Manage your subscription, payment method and invoices.</Text>
          <Button onClick={() => openPortal()}>View Stripe Customer Portal</Button>
          <FormGroup>
            <Checkbox
              checked={!!receiptEmails}
              label="Email me a receipt each time a subscription payment succeeds."
              onChange={() => toggleReceiptEmails()}
            />
          </FormGroup>
        </Panel>
        <Panel>
          <H3>Generate All Barcodes</H3>
          <Text>Generate barcodes for all products and variants in your store.</Text>

          <Box
            backgroundColor="warning20"
            border="box"
            borderRadius="normal"
            padding="medium"
          >
            <Text bold>Performance warning</Text>
            <Text>
              Depending on how many products and variants your store has, generating all barcodes may take a substantial amount of time.
            </Text>
          </Box>
          <Text color="warning40">
            Existing barcodes will be deleted and replaced. This action can't be undone. Backing up your product data is recommended before generating barcodes.
          </Text>
          <Text color="warning40">
            If another app or system relies on existing barcode values or images, you may need to update those apps and systems after new barcodes have been generated.
          </Text>

          <Checkbox
            label="I have read and understand the above warnings about barcode generation. I accept the risks and wish to proceed with generating new barcodes for all of my store's products."
            checked={acceptGenerateAllRisks}
            onChange={() => setAcceptGenerateAllRisks(!acceptGenerateAllRisks)}
          />

          { submittedGenerateAll && barcodes.status !== 'requesting' &&
            <Box
              backgroundColor="primary10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <Text>Barcodes are generating and should be complete in a few minutes. Search for products in the Labels + Print tool to see the generated barcodes.</Text>
            </Box>
          }

          <Button
            variant="primary"
            disabled={!acceptGenerateAllRisks || submittedGenerateAll}
            isLoading={submittedGenerateAll && barcodes.status === 'requesting'}
            onClick={() => handleGenerateAll()}
          >
            Generate All Barcodes
          </Button>
        </Panel>
        <button style={{ display: 'none' }} onClick={() => {throw new Error("Manually capture in Sentry");}}>.</button>
      </ToolPage>
    </>
  )
}

const mapStateToProps = state => ({
  stores: state.stores,
  settings: state.settings.settings,
  barcodes: state.barcodes,
});

const mapDispatchToProps = (dispatch) => ({
  submitSettings: (payload, newSettings) => dispatch(updateSettings(payload, newSettings)),
  generateAll: (payload) => dispatch(generateAllBarcodes(payload)),
  getPortalSession: (payload) => dispatch(paymentPortalSession(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StoreSettings);